import { useEffect, useState } from "react";

import { IconButton } from "@/components/Button/IconButton";
import { FlipHorizontal1Icon } from "@/components/Icons/FlipHorizontal1Icon";
import { FlipHorizontalIcon } from "@/components/Icons/FlipHorizontalIcon";
import { Modal } from "@/components/Modal/Modal";
import { RangeInput } from "@/components/RangeInput/RangeInput";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { useTranslation } from "@/translations";
import type { Area, MediaSize } from "@/ui-adapters/EasyCrop";
import { Cropper } from "@/ui-adapters/EasyCrop";
import { RuntimeHeight } from "@/ui-adapters/RuntimeHeight";

import { getCroppedImg } from "./cropImage";

interface ImageCropperProps {
  isOpen: boolean;
  onOpenChange: (state: boolean) => void;
  onSave: (img: File) => void;
  imgFile: File | null;
  allowPadding?: boolean;
}

const CROP_SIZE = 208;

export function ImageCropper({
  isOpen,
  onOpenChange,
  onSave,
  imgFile,
  allowPadding = true,
}: ImageCropperProps): React.ReactNode {
  const { t } = useTranslation();

  const [imgSrc, setImgSrc] = useState<string | undefined>(undefined);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [flip, setFlip] = useState({ horizontal: false, vertical: false });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [loadedMedia, setLoadedMedia] = useState<{ src: string | undefined; size: MediaSize }>();

  const mediaSize = loadedMedia && loadedMedia.src === imgSrc ? loadedMedia.size : undefined;
  // Small and rectangular images need extra zoom to fill the fixed square crop.
  let minZoom = allowPadding ? 0.1 : 1;
  if (!allowPadding && mediaSize) {
    minZoom = CROP_SIZE / Math.min(mediaSize.width, mediaSize.height);
  }
  const cropZoom = Math.max(zoom, minZoom);
  const maxZoom = Math.max(5, minZoom);

  const onCropComplete = (_: Area, newCroppedAreaPixels: Area) => {
    setCroppedAreaPixels(newCroppedAreaPixels);
  };

  const onSaveCrop = async () => {
    if (!imgFile || !imgSrc || !croppedAreaPixels) {
      return;
    }

    const newImg = await getCroppedImg(imgFile, imgSrc, croppedAreaPixels, rotation, flip, { allowPadding });
    if (!newImg) {
      return;
    }

    onSave(newImg);
    onOpenChange(false);
  };

  useEffect(() => {
    if (imgFile) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setFlip({ horizontal: false, vertical: false });
      setImgSrc(URL.createObjectURL(imgFile));
    }
  }, [imgFile]);

  return (
    <Modal.Root
      isOpened={isOpen}
      onOpenChange={onOpenChange}
      title={t("component.image-input.cropper.title")}
      animation="fade"
      isNested
    >
      <Modal.Body>
        <RuntimeHeight height={CROP_SIZE}>
          {(heightAttributes) => (
            <div {...heightAttributes} className="relative">
              <Cropper
                key={imgSrc}
                image={imgSrc}
                transform={[
                  `translate(${crop.x}px, ${crop.y}px)`,
                  `rotateZ(${rotation}deg)`,
                  `rotateY(${flip.horizontal ? 180 : 0}deg)`,
                  `rotateX(${flip.vertical ? 180 : 0}deg)`,
                  `scale(${cropZoom})`,
                ].join(" ")}
                crop={crop}
                zoom={cropZoom}
                minZoom={allowPadding ? 1 : minZoom}
                maxZoom={maxZoom}
                rotation={rotation}
                cropSize={{ width: CROP_SIZE, height: CROP_SIZE }}
                cropShape="round"
                restrictPosition={!allowPadding}
                setMediaSize={(size) => {
                  if (size.width > 0 && size.height > 0) {
                    setLoadedMedia({ src: imgSrc, size });
                  }
                }}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
              />
            </div>
          )}
        </RuntimeHeight>
        <Inline gap="md" wrap={false}>
          {/* The full-width wrapper keeps the zoom range as wide as the rotation range next to it. */}
          <div className="w-full">
            <Inline gap="sm" align="center">
              <label className="text-text-primary" htmlFor="zoom-input">
                <span className="text-body-emphasis">{t("component.image-input.cropper.zoom")}</span>
              </label>
              <RangeInput
                id="zoom-input"
                label={t("component.image-input.cropper.zoom")}
                min={minZoom}
                max={maxZoom}
                step={0.1}
                value={cropZoom}
                onChange={setZoom}
              />
            </Inline>
          </div>
          <div className="flex w-full items-center gap-space-sm">
            <label className="text-text-primary" htmlFor="rotation-input">
              <span className="text-body-emphasis">{t("component.image-input.cropper.rotation")}</span>
            </label>
            <RangeInput
              id="rotation-input"
              label={t("component.image-input.cropper.rotation")}
              min={0}
              max={360}
              step={1}
              value={rotation}
              onChange={setRotation}
            />
          </div>
          <IconButton
            title={t("component.image-input.cropper.flip-horizontal")}
            onClick={() => {
              setFlip((prev) => ({ horizontal: !prev.horizontal, vertical: prev.vertical }));
              setRotation((prev) => 360 - prev);
            }}
            icon={<FlipHorizontalIcon />}
          />
          <IconButton
            title={t("component.image-input.cropper.flip-vertical")}
            onClick={() => {
              setFlip((prev) => ({ horizontal: prev.horizontal, vertical: !prev.vertical }));
              setRotation((prev) => 360 - prev);
            }}
            icon={<FlipHorizontal1Icon />}
          />
        </Inline>
      </Modal.Body>

      <Modal.Actions>
        <Button type="button" variant="outline" tone="primary" onClick={() => onOpenChange(false)}>
          {t("common.action.cancel")}
        </Button>
        <Button
          type="button"
          variant="filled"
          onClick={onSaveCrop}
          disabled={!imgFile || !imgSrc || !croppedAreaPixels}
          data-testid="confirm-cropping-btn"
        >
          {t("common.action.save")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
